import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaChartLine,
  FaSearch,
  FaFilter,
  FaEye,
  FaCalendar,
  FaCheckCircle,
  FaUserShield,
} from "react-icons/fa";
import api from "../services/api";
import LoadingSpinner from "./LoadingSpinner";

interface Subscription {
  _id: string;
  userId?: {
    _id: string;
    name: string;
    email: string;
    role: string;
  };
  status: string;
  plan: string;
  provider: string;
  organizationName?: string;
  organizationCode?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  amountPaid?: number;
  createdAt: string;
  updatedAt: string;
}

interface SubscriptionsData {
  subscriptions: Subscription[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalSubscriptions: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  filters: {
    search: string;
    status: string;
    plan: string;
    provider: string;
    sortBy: string;
    sortOrder: string;
  };
  filterCounts: {
    total: number;
    byStatus: {
      active: number;
      canceled: number;
      past_due: number;
      incomplete: number;
      unpaid: number;
      pending: number;
      suspended: number;
      cancelled: number;
      expired: number;
    };
    byPlan: {
      individual: number;
      agency: number;
    };
    byProvider: {
      stripe: number;
      razorpay: number;
      paypal: number;
    };
  };
}

const SubscriptionsList: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptionsData, setSubscriptionsData] =
    useState<SubscriptionsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: "",
    status: "",
    plan: "",
    provider: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  useEffect(() => {
    fetchSubscriptions();
  }, [filters]);

  const fetchSubscriptions = async () => {
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value.toString());
      });

      const response = await api.get(`/admin/subscriptions?${queryParams}`);
      setSubscriptionsData(response.data.data || response.data);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "canceled":
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "past_due":
        return "bg-yellow-100 text-yellow-800";
      case "incomplete":
        return "bg-orange-100 text-orange-800";
      case "unpaid":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-blue-100 text-blue-800";
      case "suspended":
        return "bg-gray-100 text-gray-800";
      case "expired":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case "individual":
        return "bg-blue-100 text-blue-800";
      case "agency":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getProviderColor = (provider: string) => {
    switch (provider) {
      case "stripe":
        return "bg-blue-100 text-blue-800";
      case "razorpay":
        return "bg-purple-100 text-purple-800";
      case "paypal":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return "$0.00";
    return `$${amount.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full max-w-full overflow-hidden">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-primary to-primaryDark rounded-2xl p-4 sm:p-6 lg:p-8 text-white w-full"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 truncate">
              Subscriptions
            </h1>
            <p className="text-primaryLight text-sm sm:text-base lg:text-lg">
              Manage and monitor all platform subscriptions
            </p>
          </div>
          <div className="hidden lg:block flex-shrink-0">
            <FaChartLine className="text-4xl lg:text-6xl text-white opacity-20" />
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 w-full"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          <FaFilter className="h-5 w-5 text-gray-400" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search organizations..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="canceled">Canceled</option>
            <option value="past_due">Past Due</option>
            <option value="incomplete">Incomplete</option>
            <option value="unpaid">Unpaid</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
            <option value="expired">Expired</option>
          </select>

          {/* Plan Filter */}
          <select
            value={filters.plan}
            onChange={(e) => handleFilterChange("plan", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">All Plans</option>
            <option value="individual">Individual</option>
            <option value="agency">Agency</option>
          </select>

          {/* Provider Filter */}
          <select
            value={filters.provider}
            onChange={(e) => handleFilterChange("provider", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">All Providers</option>
            <option value="stripe">Stripe</option>
            <option value="razorpay">Razorpay</option>
            <option value="paypal">PayPal</option>
          </select>
        </div>
      </motion.div>

      {/* Stats Cards */}
      {subscriptionsData?.filterCounts && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 w-full"
        >
          <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {subscriptionsData.filterCounts.total}
                </p>
              </div>
              <FaChartLine className="h-8 w-8 text-primary" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl lg:text-3xl font-bold text-green-600">
                  {subscriptionsData.filterCounts.byStatus.active}
                </p>
              </div>
              <FaCheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Individual</p>
                <p className="text-2xl lg:text-3xl font-bold text-blue-600">
                  {subscriptionsData.filterCounts.byPlan.individual}
                </p>
              </div>
              <FaUserShield className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Agency</p>
                <p className="text-2xl lg:text-3xl font-bold text-purple-600">
                  {subscriptionsData.filterCounts.byPlan.agency}
                </p>
              </div>
              <FaUserShield className="h-8 w-8 text-purple-500" />
            </div>
          </div>
        </motion.div>
      )}

      {/* Subscriptions List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 w-full"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            All Subscriptions
          </h3>
          <div className="flex items-center space-x-2">
            <FaFilter className="h-4 w-4 text-gray-400" />
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split("-");
                handleFilterChange("sortBy", sortBy);
                handleFilterChange("sortOrder", sortOrder);
              }}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="status-asc">Status A-Z</option>
              <option value="status-desc">Status Z-A</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  User
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Organization
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Status
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Plan
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Provider
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Amount Paid
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Created
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {subscriptionsData?.subscriptions.map((subscription) => (
                <tr
                  key={subscription._id}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-gray-900">
                        {subscription.userId?.name || "N/A"}
                      </p>
                      <p className="text-sm text-gray-500">
                        {subscription.userId?.email || "N/A"}
                      </p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-gray-900">
                        {subscription.organizationName || "N/A"}
                      </p>
                      {subscription.organizationCode && (
                        <p className="text-sm text-gray-500">
                          Code: {subscription.organizationCode}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                        subscription.status
                      )}`}
                    >
                      {subscription.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPlanColor(
                        subscription.plan
                      )}`}
                    >
                      {subscription.plan}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getProviderColor(
                        subscription.provider
                      )}`}
                    >
                      {subscription.provider}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-gray-900">
                      {formatAmount(subscription.amountPaid)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <FaCalendar className="mr-1" />
                      {formatDate(subscription.createdAt)}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => {
                        if (subscription.userId?._id) {
                          navigate(`/users/${subscription.userId._id}`);
                        }
                      }}
                      disabled={!subscription.userId?._id}
                      className="flex items-center text-primary hover:text-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaEye className="mr-1" />
                      <span className="text-sm">View User</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {subscriptionsData?.pagination && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-500">
              Showing{" "}
              {(subscriptionsData.pagination.currentPage - 1) *
                subscriptionsData.pagination.limit +
                1}{" "}
              to{" "}
              {Math.min(
                subscriptionsData.pagination.currentPage *
                  subscriptionsData.pagination.limit,
                subscriptionsData.pagination.totalSubscriptions
              )}{" "}
              of {subscriptionsData.pagination.totalSubscriptions} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() =>
                  handlePageChange(subscriptionsData.pagination.currentPage - 1)
                }
                disabled={!subscriptionsData.pagination.hasPrevPage}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {subscriptionsData.pagination.currentPage} of{" "}
                {subscriptionsData.pagination.totalPages}
              </span>
              <button
                onClick={() =>
                  handlePageChange(subscriptionsData.pagination.currentPage + 1)
                }
                disabled={!subscriptionsData.pagination.hasNextPage}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SubscriptionsList;
